import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { PlayerRef } from '@remotion/player';
import { useVideoContext } from '../../../../hooks/use-video';
import {
  RemotionConfig,
  TrackGroup,
  TrackItem,
  TrackType,
} from '@monorepo/remotion-ninja';
import TimelineControls from './timeline-controls';
import TimelineMarkers from './timeline-markers';
import TrackGroupComponent from './track-group';
import './multi-track-timeline.scss';

interface MultiTrackTimelineProps {
  playerRef: PlayerRef;
  config: RemotionConfig;
  onTrackItemClick?: (item: TrackItem) => void;
  onTrackGroupChange?: (trackGroups: TrackGroup[]) => void;
}

const MultiTrackTimeline: React.FC<MultiTrackTimelineProps> = ({
  playerRef,
  config,
  onTrackItemClick,
  onTrackGroupChange,
}) => {
  const { isPlayerReady } = useVideoContext();
  const [currentFrame, setCurrentFrame] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const timelineRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoomLevel, setZoomLevel] = useState(3);
  const isMouseDownRef = useRef(false);

  // Track management
  const [trackGroups, setTrackGroups] = useState<TrackGroup[]>(
    config.trackGroups || []
  );
  const [trackVisibility, setTrackVisibility] = useState<
    Record<string, boolean>
  >({});
  const [trackLocks, setTrackLocks] = useState<Record<string, boolean>>({});

  // Initialize track states from config
  useEffect(() => {
    if (config.trackGroups) {
      setTrackGroups(config.trackGroups);

      // Initialize visibility and lock states
      const newVisibility: Record<string, boolean> = {};
      const newLocks: Record<string, boolean> = {};

      config.trackGroups.forEach((group, index) => {
        const trackId = `track-${group.type}-${index}`;
        newVisibility[trackId] = true;
        newLocks[trackId] = false;
      });

      setTrackVisibility(newVisibility);
      setTrackLocks(newLocks);
    }
  }, [config.trackGroups]);

  // Handle playback state
  useEffect(() => {
    if (!playerRef) return;

    const interval = setInterval(() => {
      if (isPlaying && !isDragging) {
        const frame = playerRef.getCurrentFrame();
        setCurrentFrame(frame);
      }
    }, 1000 / (config.fps || 30));

    return () => clearInterval(interval);
  }, [isPlaying, isDragging, config.fps]);

  useEffect(() => {
    if (!playerRef) return;

    const onPlay = () => {
      setIsPlaying(true);
    };

    const onPause = () => {
      setIsPlaying(false);
    };

    const onTimeUpdate = (e) => {
      const seekedFrame = e.detail.frame;
      setCurrentFrame(seekedFrame);
    };

    playerRef.addEventListener('play', onPlay);
    playerRef.addEventListener('pause', onPause);
    playerRef.addEventListener('frameupdate', onTimeUpdate);

    return () => {
      playerRef.removeEventListener('play', onPlay);
      playerRef.removeEventListener('pause', onPause);
      playerRef.removeEventListener('frameupdate', onTimeUpdate);
    };
  }, [isPlayerReady]);

  // Set up event listeners to detect mouse dragging
  useEffect(() => {
    const handleMouseDown = () => {
      isMouseDownRef.current = true;
    };

    const handleMouseUp = () => {
      isMouseDownRef.current = false;
    };

    // Add global event listeners
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  // Improved auto-scroll timeline during playback
  useEffect(() => {
    if (!containerRef.current || !timelineRef.current || !isPlaying) return;

    const container = containerRef.current;
    const timelineRect = timelineRef.current.getBoundingClientRect();
    const playheadPosition =
      (currentFrame / config.durationInFrames) * timelineRect.width;

    // Calculate visible area
    const visibleStart = container.scrollLeft;
    const visibleEnd = visibleStart + container.clientWidth;

    const isUserDraggingOrScrolling = isMouseDownRef.current || isDragging;

    if (
      (playheadPosition < visibleStart || playheadPosition > visibleEnd) &&
      !isUserDraggingOrScrolling
    ) {
      const newScrollPosition = playheadPosition - container.clientWidth / 2;
      container.scrollTo({
        left: Math.max(0, newScrollPosition),
        behavior: 'instant', // Use instant behavior for seeking
      });
    }
  }, [currentFrame, isPlaying, config.durationInFrames]);

  const calculateFrame = useCallback(
    (clientX: number): number => {
      if (!timelineRef.current) return 0;

      const rect = timelineRef.current.getBoundingClientRect();
      const trackStart = rect.left;
      const trackEnd = rect.right;
      const trackWidth = trackEnd - trackStart;

      const x = Math.max(0, Math.min(clientX - trackStart, trackWidth));
      const percentage = x / trackWidth;
      return Math.floor(percentage * config.durationInFrames);
    },
    [config.durationInFrames]
  );

  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsDragging(true);

      const handleDrag = (e: MouseEvent) => {
        e.preventDefault();
        const frame = calculateFrame(e.clientX);
        playerRef?.seekTo(frame);
      };

      const handleDragEnd = () => {
        setIsDragging(false);
        document.removeEventListener('mousemove', handleDrag);
        document.removeEventListener('mouseup', handleDragEnd);
      };

      document.addEventListener('mousemove', handleDrag);
      document.addEventListener('mouseup', handleDragEnd);
    },
    [calculateFrame]
  );

  const handleTimelineClick = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging) {
        const frame = calculateFrame(e.clientX);
        playerRef?.seekTo(frame);
      }
    },
    [isDragging, calculateFrame]
  );

  // Add a new track at the top
  const addTrack = () => {
    const newTrackGroup: TrackGroup = {
      type: TrackType.Track,
      tracks: [],
      index: 0,
    };

    // Reindex existing tracks
    const updatedTrackGroups = trackGroups.map((group) => ({
      ...group,
      index: group.index + 1,
    }));

    // Add new track at the beginning
    const finalTrackGroups = [newTrackGroup, ...updatedTrackGroups];
    setTrackGroups(finalTrackGroups);

    // Set visibility and lock for the new track
    const trackId = `track-${newTrackGroup.type}-${newTrackGroup.index}`;
    setTrackVisibility((prev) => ({
      ...prev,
      [trackId]: true,
    }));

    setTrackLocks((prev) => ({
      ...prev,
      [trackId]: false,
    }));

    if (onTrackGroupChange) {
      onTrackGroupChange(finalTrackGroups);
    }
  };

  // Calculate timeline width based on zoom level
  const getTimelineWidth = () => {
    const baseWidth = 100; // Base percentage width
    return `${baseWidth * zoomLevel}%`;
  };

  // Memoized track groups to prevent unnecessary re-renders
  const memoizedTrackGroups = useMemo(() => {
    return trackGroups.map((trackGroup, groupIndex) => {
      const trackId = `track-${trackGroup.type}-${groupIndex}`;
      const isVisible = trackVisibility[trackId] !== false;
      const isLocked = trackLocks[trackId] === true;

      return {
        trackGroup,
        groupIndex,
        trackId,
        isVisible,
        isLocked,
      };
    });
  }, [trackGroups, trackVisibility, trackLocks]);

  return (
    <div className={`multi-track-timeline ${isDragging ? 'dragging' : ''}`}>
      <TimelineControls
        playerRef={playerRef}
        isPlaying={isPlaying}
        totalFrames={config.durationInFrames}
        currentFrame={currentFrame}
        setIsPlaying={setIsPlaying}
        zoom={zoomLevel}
        setZoom={setZoomLevel}
      />

      <div className="timeline-layout">
        {/* Empty timeline actions area - reserved for future use */}
        <div className="timeline-actions">{/* Completely empty now */}</div>

        {/* Timeline content */}
        <div ref={containerRef} className="timeline-scroll-container">
          <div
            className="timeline-wrapper"
            style={{ width: getTimelineWidth() }}
          >
            <div className="timeline-marker-container">
              <TimelineMarkers
                zoom={zoomLevel}
                totalFrames={config.durationInFrames}
                fps={config.fps || 30}
              />
            </div>

            <div ref={timelineRef} className="tracks-container">
              {memoizedTrackGroups.map(
                ({ trackGroup, groupIndex, trackId, isVisible, isLocked }) => (
                  <TrackGroupComponent
                    key={trackId}
                    trackGroup={trackGroup}
                    trackId={trackId}
                    isVisible={isVisible}
                    isLocked={isLocked}
                    totalFrames={config.durationInFrames}
                    onItemClick={onTrackItemClick}
                    zoomLevel={zoomLevel}
                    onClick={handleTimelineClick}
                  />
                )
              )}
            </div>

            {/* Playhead moved back to timeline-wrapper for full height */}
            <div
              className="playhead"
              style={{
                left: `${(currentFrame / config.durationInFrames) * 100}%`,
              }}
            >
              <div className="playhead-marker" onMouseDown={handleDragStart} />
              <div className="playhead-line" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiTrackTimeline;
